/**
 * Dynamic plan pricing (frontend mirror of backend planPricing.js).
 */

export const GST_RATE = 0.18;
export const YEARLY_DISCOUNT = 0.1;
export const INR_TO_USD = 85;

export const FORWARD_RATES_ADDON = {
  INR: 9999,
  USD: 119,
};

export const PLAN_PRICING = {
  FREE: {
    basePriceINR: 0,
    includedCalls: 100,
    additionalRateINR: 0,
    minCalls: 100,
    maxCalls: 100,
  },
  BASIC: {
    basePriceINR: 849,
    basePriceUSD: 10,
    includedCalls: 35000,
    additionalRateINR: 0.002,
    minCalls: 35000,
    maxCalls: 500000,
  },
  ESSENTIAL: {
    basePriceINR: 2999,
    basePriceUSD: 35,
    includedCalls: 1500000,
    additionalRateINR: 0.00175,
    minCalls: 1500000,
    maxCalls: 3000000,
  },
  PRO: {
    basePriceINR: 8499,
    basePriceUSD: 99,
    includedCalls: 3500000,
    additionalRateINR: 0.0015,
    minCalls: 3500000,
    maxCalls: 10000000,
  },
  ULTIMATE: {
    basePriceINR: 24999,
    basePriceUSD: 294,
    includedCalls: 15000000,
    additionalRateINR: 0.00125,
    minCalls: 15000000,
    maxCalls: 30000000,
  },
};

export function getPlanPricingConfig(planName) {
  const key = String(planName || "").toUpperCase();
  return PLAN_PRICING[key] || null;
}

export function clampApiCalls(planName, apiCalls) {
  const cfg = getPlanPricingConfig(planName);
  if (!cfg) return null;
  const n = parseInt(apiCalls, 10);
  if (!Number.isFinite(n)) return null;
  return Math.min(Math.max(n, cfg.minCalls), cfg.maxCalls);
}

export function roundUsdAmount(value) {
  const n = Number(value) || 0;
  return Math.round(n * 100) / 100;
}

export function roundPlanChargeAmount(amount, currency = "INR") {
  const n = Number(amount) || 0;
  if (String(currency).toUpperCase() === "USD") {
    return roundUsdAmount(n);
  }
  return Math.round(n);
}

export function calculatePlanPrice(
  planName,
  apiCalls,
  { interval = "monthly", currency = "INR", forwardRatesAddon = false } = {}
) {
  const cfg = getPlanPricingConfig(planName);
  if (!cfg) throw new Error(`Unknown plan: ${planName}`);

  const calls = clampApiCalls(planName, apiCalls);
  if (calls == null) throw new Error("Invalid API calls value");

  const additionalCalls = Math.max(0, calls - cfg.includedCalls);
  let subtotalINR =
    cfg.basePriceINR + additionalCalls * cfg.additionalRateINR;

  const baseUSD = cfg.basePriceUSD ?? cfg.basePriceINR / INR_TO_USD;
  const additionalRateUSD = cfg.additionalRateINR / INR_TO_USD;
  let subtotalUSD = baseUSD + additionalCalls * additionalRateUSD;

  if (forwardRatesAddon) {
    subtotalINR += FORWARD_RATES_ADDON.INR;
    subtotalUSD += FORWARD_RATES_ADDON.USD;
  }

  if (String(interval).toLowerCase() === "yearly") {
    const yearlyFactor = 12 * (1 - YEARLY_DISCOUNT);
    subtotalINR *= yearlyFactor;
    subtotalUSD *= yearlyFactor;
  }

  subtotalINR = Math.round(subtotalINR);
  subtotalUSD = roundUsdAmount(subtotalUSD);
  const gstINR = Math.round(subtotalINR * GST_RATE);
  const totalINR = subtotalINR + gstINR;

  const amount =
    String(currency).toUpperCase() === "USD" ? subtotalUSD : totalINR;

  return {
    amount,
    apiCalls: calls,
    additionalCalls,
    subtotalINR,
    subtotalUSD,
    gstINR,
    totalINR,
    currency: String(currency).toUpperCase(),
    interval: String(interval).toLowerCase(),
  };
}

export function getGlobalApiCallsBounds() {
  const paid = ["BASIC", "ESSENTIAL", "PRO", "ULTIMATE"];
  const mins = paid.map((p) => PLAN_PRICING[p].minCalls);
  const maxs = paid.map((p) => PLAN_PRICING[p].maxCalls);
  return {
    minCalls: Math.min(...mins),
    maxCalls: Math.max(...maxs),
  };
}

export function validateApiCallsForPlan(planName, apiCalls) {
  const cfg = getPlanPricingConfig(planName);
  if (!cfg) return { valid: false, error: "Unknown plan" };

  if (apiCalls === "" || apiCalls == null) {
    return {
      valid: false,
      error: `Enter between ${cfg.minCalls.toLocaleString()} and ${cfg.maxCalls.toLocaleString()} API calls`,
      minCalls: cfg.minCalls,
      maxCalls: cfg.maxCalls,
    };
  }

  const n = parseInt(String(apiCalls).replace(/,/g, ""), 10);
  if (!Number.isFinite(n)) {
    return {
      valid: false,
      error: "Enter a valid number",
      minCalls: cfg.minCalls,
      maxCalls: cfg.maxCalls,
    };
  }

  if (n < cfg.minCalls) {
    return {
      valid: false,
      error: `Minimum ${cfg.minCalls.toLocaleString()} API calls required`,
      minCalls: cfg.minCalls,
      maxCalls: cfg.maxCalls,
    };
  }

  if (n > cfg.maxCalls) {
    return {
      valid: false,
      error: `Maximum ${cfg.maxCalls.toLocaleString()} API calls allowed`,
      minCalls: cfg.minCalls,
      maxCalls: cfg.maxCalls,
    };
  }

  return {
    valid: true,
    apiCalls: n,
    minCalls: cfg.minCalls,
    maxCalls: cfg.maxCalls,
  };
}

export function formatPlanDisplayPrice(amount, currency = "INR") {
  const n = Number(amount) || 0;
  if (String(currency).toUpperCase() === "USD") {
    const v = roundUsdAmount(n);
    return v % 1 === 0
      ? v.toLocaleString()
      : v.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }
  return Math.round(n).toLocaleString();
}

export function roundPlanDisplayAmount(amount, currency = "INR") {
  return roundPlanChargeAmount(amount, currency);
}
